import { __ } from '@wordpress/i18n';
import { setupLocationVisibility } from './cityFilter';

const GROUPS = [
	{
		label: __( 'Basic information', 'anacrm' ),
		open: false,
		fields: [
			'_re_description',
			'_re_sector',
			'_re_work_scope',
			'_re_field',
			'_re_keyword',
			'_re_resource_type',
			'_re_use_term',
			'_re_duration',
		],
	},
	{
		label: __( 'Location', 'anacrm' ),
		fields: [
			'_re_location',
			'_re_territory',
			'_re_region',
			'_re_city',
			'_re_address',
		],
	},
	{
		label: __( 'Contact', 'anacrm' ),
		fields: [
			'_re_person_ref',
			'_re_org_ref',
			'_re_contact_mail',
			'_re_contact_phone',
			'_re_contact_social',
		],
	},
	{
		label: __( 'Relations', 'anacrm' ),
		fields: [ '_re_org', '_re_project', '_re_person' ],
	},
];

function findFieldWrapper( form, name ) {
	const dashedName = name.replace( /_/g, '-' );
	return form.querySelector( `.pods-form-ui-row-name-${ dashedName }` );
}

const REQUIRED_PICK_FIELDS = [
	'_re_work_scope',
	'_re_keyword',
	'_re_resource_type',
];

function markFieldRequired( wrapper ) {
	// Front-end Pods forms render fields in "embed" mode, which skips the
	// admin-only DFV FieldLabel React component entirely (no
	// .pods-field-label__* markup exists here). The visible label is
	// server-rendered PHP instead (PodsForm::label(), ui/fields/_label.php),
	// with class "pods-form-ui-label" — that's what we mark up here, instead
	// of setting the field's `required` option in PHP (which would also
	// switch on Pods' native required-validation UI and produce a second,
	// redundant error message alongside setupRequiredPickFieldsValidation()
	// below).
	const label = wrapper?.querySelector( '.pods-form-ui-label' );
	if ( ! label || label.querySelector( '.anacrm-required-marker' ) ) {
		return;
	}
	const marker = document.createElement( 'abbr' );
	marker.className = 'anacrm-required-marker text-red no-underline';
	marker.title = __( 'required', 'anacrm' );
	marker.textContent = ' *';
	label.appendChild( marker );
}

function hasPickValue( form, fieldName ) {
	// Pods (DFV/react-select) renders one hidden input per selected term,
	// named pods_field_{field}[0], [1], ... and renders none at all when
	// the field is empty — there is no single always-present value holder.
	return (
		form.querySelectorAll(
			`input[type="hidden"][name^="pods_field_${ fieldName }["]`
		).length > 0
	);
}

function setupRequiredPickFieldsValidation( form ) {
	let errorEl = null;

	function showError() {
		if ( errorEl ) {
			return;
		}
		errorEl = document.createElement( 'div' );
		errorEl.className = 'w-full px-4 py-3 mb-4 bg-red text-black';
		errorEl.setAttribute( 'role', 'alert' );
		errorEl.textContent = __(
			'Please fill in work scope, keywords and resource type.',
			'anacrm'
		);
		form.insertBefore( errorEl, form.firstChild );
	}

	function clearError() {
		if ( errorEl ) {
			errorEl.remove();
			errorEl = null;
		}
	}

	function isValid() {
		return REQUIRED_PICK_FIELDS.every( ( name ) =>
			hasPickValue( form, name )
		);
	}

	function block( e ) {
		e.preventDefault();
		e.stopPropagation();
		e.stopImmediatePropagation();
		showError();
		const missing = REQUIRED_PICK_FIELDS.find(
			( name ) => ! hasPickValue( form, name )
		);
		findFieldWrapper( form, missing )?.scrollIntoView( {
			block: 'center',
		} );
	}

	// These fields are react-select widgets: selecting/removing a term adds
	// or removes a hidden input via React, which never dispatches a native
	// "input" event, so a MutationObserver — not an input listener — is
	// what notices the change and clears a shown error live.
	const observer = new MutationObserver( () => {
		if ( errorEl && isValid() ) {
			clearError();
		}
	} );
	observer.observe( form, { childList: true, subtree: true } );

	// Same two-listener capture-phase pattern used for person's required-name
	// check: Pods binds its submit-button click handler with jQuery
	// delegation in the bubble phase and replays it as a synthetic
	// jQuery-only submit that never dispatches a real "submit" event, so a
	// capture-phase click on the submit control is needed in addition to a
	// capture-phase submit listener (the fallback for Enter-key submission,
	// which does dispatch a real native "submit" event).
	form.addEventListener(
		'click',
		( e ) => {
			if (
				! e.target.closest(
					'input[type="submit"], button[type="submit"]'
				)
			) {
				return;
			}
			if ( isValid() ) {
				clearError();
				return;
			}
			block( e );
		},
		true
	);

	form.addEventListener(
		'submit',
		( e ) => {
			if ( isValid() ) {
				clearError();
				return;
			}
			block( e );
		},
		true
	);
}

function buildAccordion( form ) {
	for ( const group of GROUPS ) {
		const fieldWrappers = group.fields
			.map( ( f ) => findFieldWrapper( form, f ) )
			.filter( Boolean );
		if ( ! fieldWrappers.length ) {
			continue;
		}

		const parent = fieldWrappers[ 0 ].parentElement;

		const details = document.createElement( 'details' );
		if ( group.open ) {
			details.open = true;
		}
		details.className = 'mb-4 border-2 border-green';

		const summary = document.createElement( 'summary' );
		summary.className =
			'px-4 py-3 cursor-pointer select-none bg-green/30 hover:bg-green/60 marker:float-left';
		const summaryTit = document.createElement( 'h2' );
		summaryTit.className = 'text-base font-semibold inline-block';
		summaryTit.textContent = group.label;
		summary.appendChild( summaryTit );
		const summaryDesc = document.createElement( 'p' );
		summaryDesc.textContent = group.description ?? '';
		summary.appendChild( summaryDesc );
		details.appendChild( summary );

		const body = document.createElement( 'div' );
		body.className = 'px-4 py-2 bg-green/10';
		details.appendChild( body );

		parent.insertBefore( details, fieldWrappers[ 0 ] );
		fieldWrappers.forEach( ( w ) => body.appendChild( w ) );
	}
}

document.addEventListener( 'DOMContentLoaded', () => {
	const wrapper = document.querySelector( '.anacrm-resource-form-accordion' );
	if ( ! wrapper ) {
		return;
	}

	const successDiv = wrapper.querySelector( '.pods-form-front-success' );
	if ( successDiv ) {
		const btn = document.createElement( 'button' );
		btn.type = 'button';
		btn.className = 'px-3 py-2 text-2xl hover:text-yellow cursor-pointer';
		btn.innerHTML = '&times;';
		btn.addEventListener( 'click', () => successDiv.remove() );
		successDiv.appendChild( btn );
	}

	const form = wrapper.querySelector( 'form' );
	if ( ! form ) {
		return;
	}

	setupRequiredPickFieldsValidation( form );

	let done = false;

	function tryBuild() {
		if ( done ) {
			return;
		}
		if ( ! form.querySelector( '.pods-field__container' ) ) {
			return;
		}
		done = true;
		observer.disconnect();
		buildAccordion( form );
		REQUIRED_PICK_FIELDS.forEach( ( name ) =>
			markFieldRequired( findFieldWrapper( form, name ) )
		);
		setupLocationVisibility( form, {
			locationField: '_re_location',
			territoryField: '_re_territory',
			regionField: '_re_region',
			cityField: '_re_city',
			addressField: '_re_address',
		} );
	}

	let debounceTimer = null;
	const observer = new MutationObserver( () => {
		clearTimeout( debounceTimer );
		debounceTimer = setTimeout( tryBuild, 300 );
	} );

	observer.observe( form, { childList: true, subtree: true } );

	tryBuild();
} );
